/* Cœura Carigram renderer (Shopify). Draws the personalization DIGITALLY as an SVG overlay
   composited over a blank product photo — the real Sappho monogram for initials, the actual
   moon phase for a date, engraved script/caps for words. No AI-per-variant images.
   Ported from the mockup's js/carigram.js. Coordinates are in the 0-100 engrave-box space.
   v2 (9/11, Engraving Bench): see the v2 block below: per-placement plates, image-percent boxes,
   and line-by-line engraving layout driven by parameters Patti sets in the bench. */
(function () {
  'use strict';
  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

  /* Where this file lives, so the moon faces can be reached without every caller threading an
     asset base through. document.currentScript is read at load time, while the script is running. */
  var ASSET_BASE = (function () {
    var sc = document.currentScript;
    var src = (sc && sc.src) || '';
    return src.replace(/carigram-render\.js(\?.*)?$/, '');
  })();

  /* Pieces whose moon phase is PHOTOGRAPHED rather than drawn: the full lit disc, shot as part of
     the real piece, which the renderer clips to the terminator for the customer's night.
     Keyed by product HANDLE, not by form: the Locket and the Pendant share the form 'moon-pendant',
     and a bench entry carries whatever form it was seeded with. A handle that isn't listed falls
     back to the flat gradient below, so every other piece is unchanged. */
  var MOON_FACES = {
    'the-moon-locket': { silver: 'moon-face-locket-0916-silver.jpg', vermeil: 'moon-face-locket-0916-vermeil.jpg' },
    'the-moon-pendant': { silver: 'moon-face-pendant-0916-silver.jpg', vermeil: 'moon-face-pendant-0916-vermeil.jpg' },
    /* The Medallion (CH015, 1 1/4") wears the Pendant's moon, scaled to its disc, so the three Moon pages read
       as one piece in three sizes (Patti, 10/14). Its own cast is bolder; a photographed Medallion face can
       replace these later. A full URL (a product photo) would also be accepted here, see moonFace(). */
    'the-moon-medallion': { silver: 'moon-face-pendant-0916-silver.jpg', vermeil: 'moon-face-pendant-0916-vermeil.jpg' }
  };
  /* The date in the name is the day the asset was BUILT, and it is there so a changed picture can
     never reuse a URL. Theme images are served from /cdn/shop/t/<n>/assets/<name> with no version
     query and a year-long max-age, and the WebP variant a browser actually asks for is cached
     separately again — so overwriting one in place leaves customers on the old picture for an
     unknowable time. Rebuild from new photography, bump the date, and every viewer gets it at once.
     Superseded discs stay on the theme untouched, which is what makes rollback one line here. */
  /* Pieces whose phases John has RENDERED, one image per night of the month, rather than anything
     we composite. When a set exists for the piece and metal, that image IS the product shot for the
     customer's night: the stage swaps to it and no moon is drawn over it. A piece or metal with no
     set falls through to the plate-plus-disc composite below, so the two can coexist while the
     remaining sheets are still being made.
     `n` is how many nights the sheet covers, `vh` its height as a percent of its width. The date in
     the prefix is the day the frames were CUT — re-cropping and reusing a name serves the old crop
     from cache for an unknowable time, which is how the first set went out with "19th Day" printed
     across the top of every frame. `slice_sheet.py` prints this whole entry. */
  var MOON_FRAMES = {
    /* EMPTY ON PURPOSE. John's 9/16 sheet was tried here and pulled straight back out: each night is
       only 160x200, and the product page shows the piece past 1000px wide, so it went out a six-fold
       upscale ("these images are too blurry", Kevin 9/16). Cropping tight to the piece also made it
       fill the frame, where the composite's own plate gives it room. Drop in a set at the
       photography's resolution and this route lights up again — nothing else needs to change.
         'the-moon-locket': { vermeil: { prefix: '...-', n: 28, ext: '.png', vh: 125.0 } } */
  };
  var SYNODIC = 29.530588853;
  /* A night as the customer typed it ("2026-01-10") becomes a moment. 22:00 UTC, because that is
     mid-evening where the sky is being remembered and it keeps the date off both midnights.
     One place, because COMoon.age() takes a Date and silently throws on a string. */
  function nightDate(night) {
    if (!night) return null;
    var d = new Date(night + 'T22:00:00Z');
    return isNaN(d) ? null : d;
  }
  /* The night's frame. The sheet's own captions run "1st Day … 28th Day" and its brightest frame is
     day 14, so the month is laid on it linearly with day 14 at full — a 28-frame sheet cannot line
     up with a 29.53-day month exactly, and rounding to the nearest night is the honest fit.
     Age 0 (new) rounds to 0, which is the same night as `n`. */
  function hasMoonFrames(handle, metal) {
    return !!(MOON_FRAMES[handle] && MOON_FRAMES[handle][metal]);
  }
  function moonFrame(handle, metal, age) {
    var set = MOON_FRAMES[handle] && MOON_FRAMES[handle][metal];
    if (!set || age == null || isNaN(age)) return null;
    var d = Math.round(((age % SYNODIC) + SYNODIC) % SYNODIC / SYNODIC * set.n) % set.n;
    if (d === 0) d = set.n;
    return { src: ASSET_BASE + set.prefix + (d < 10 ? '0' + d : String(d)) + set.ext, day: d, vh: set.vh };
  }

  function moonFace(handle, metal) {
    var set = MOON_FACES[handle];
    if (!set) return null;
    var f = set[metal] || set.silver;
    return /^(https?:)?\/\//.test(f) ? f : ASSET_BASE + f;   // a full URL (a product photo) is used as-is
  }

  /* The moon for one night, inside a box whose inscribed circle IS the moon's disc.
     With a face photo: the full lit disc, masked to moon.js's terminator, over the druzy plate.
     Without one: the old flat gradient, so pieces with no photographed face are unchanged. */

  /* The silver/druzy boundary, as fractions of the moon's radius r so it follows the piece at any
     size. Measured on the 3000px product photo, where r = 433.
       grain   - crystal wavelength (~15px)
       scatter - depth of the band where stone and silver interlock (~45px)
       oct     - turbulence octaves; more = finer prickle, fewer = chunkier crystal
       amp     - how much noise is mixed into the edge ramp
       slope   - how hard that mix is thresholded. High (24) gives a crisp crystal frontier and the
                 silver reads as INLAID under the stone. Low (10) leaves a graded, grainy,
                 part-opaque band, so it reads as PAINTED over the crystal, thinning at its edge
                 until the stone comes through — which is the real piece (Kevin 9/14). Below ~5 it
                 stops reading as paint and just looks blurred.
       rim     - width of the solid band held at the moon's OUTER line. The dissolve softens every
                 boundary of the lit shape, but only the TERMINATOR is painted out into the stone;
                 the outer line is solid in the real piece (Kevin 9/14). So the mask re-draws the
                 shape crisp inside a ring at the limb, over the dissolved copy.
       shadow  - contact shadow under the paint. The druzy is already dark, so this stays very low;
                 anything heavier draws exactly the solid outline the real piece lacks. */
  var EDGE = { grain: 0.05, scatter: 0.07, oct: 2, seed: 11, amp: 0.5, slope: 10, rim: 0.1, shadow: 0.1 };

  /* A flat opacity was the wrong tool for "faded into the locket": over near-black druzy a 15%
     blend costs ~22 levels and just reads as a dark moon ("too dark", Kevin 9/14). The silver keeps
     its full colour; only its edge thins. Left here, off, because it is the obvious wrong turn. */
  var MOON_FADE = 0;

  /* The disc is drawn to the inscribed circle of the bench box, but on the finished pieces the dark
     druzy setting is wider than that, so the moon sat small with a thick black ring around it and read
     dull rather than metallic (Kevin 9/18: "more metallic, a little bigger, too much black margin").
     Growing the disc shows more of the bright silver moon, which is what makes it read metallic. One
     tunable factor, applied at every draw; ~1.0 = inscribed circle (small, thick black ring), 1.26 =
     flush to the bezel (no ring). Kevin 9/18 wants a LITTLE black margin left — a thin ring between
     the two — so this sits in the low-mid of that range. */
  var MOON_FILL = 1.14;

  /* an annulus as one even-odd path: outer circle, then inner circle */
  function circle(cx, cy, r) {
    return 'M ' + (cx - r).toFixed(3) + ' ' + cy.toFixed(3) +
      ' A ' + r.toFixed(3) + ' ' + r.toFixed(3) + ' 0 1 0 ' + (cx + r).toFixed(3) + ' ' + cy.toFixed(3) +
      ' A ' + r.toFixed(3) + ' ' + r.toFixed(3) + ' 0 1 0 ' + (cx - r).toFixed(3) + ' ' + cy.toFixed(3) + ' Z';
  }
  function ring(cx, cy, ro, ri) { return circle(cx, cy, ro) + ' ' + circle(cx, cy, ri); }

  /* The thinnest crescent the piece will ever show, as an illuminated fraction.
     A real new moon is not dark for one night — it reads as nothing for about five, because the
     sliver either side is a fraction of a percent of the disc and there is no way to cast it. So
     the moon HOLDS at its thinnest crescent through that window instead of disappearing (Kevin
     9/16: "two days before and after the moon is completely gone, should match the nearest
     crescent"). September 2026 is the worked example: the 8th is 9.5% lit and the 14th 10.2%, while
     the 9th, 10th, 12th and 13th fall to 4.2, 1.0, 1.3 and 4.7 — every one of them now renders as
     that same 9.5% crescent, on its own side of new.
     Note this leaves NO fully dark night: the new moon itself shows the thinnest waxing crescent. */
  var MIN_ILLUM = 0.095;

  /* The age to DRAW for the age that was, holding the crescent open across new. Illumination is
     (1 - cos(2*pi*p)) / 2, so a floor on it is a ceiling on cos, applied on whichever side of the
     month the night falls so a waning night stays waning. */
  function displayAge(age) {
    var S = 29.530588853;
    var p = ((age % S) + S) % S / S;
    var k = Math.cos(2 * Math.PI * p), kMax = 1 - 2 * MIN_ILLUM;
    if (k <= kMax) return age;
    var q = Math.acos(kMax) / (2 * Math.PI);
    return (p <= 0.5 ? q : 1 - q) * S;
  }

  var moonUID = 0;
  function moonSVG(age, cx, cy, r, handle, metal) {
    age = displayAge(age);
    r = r * MOON_FILL;   // fill the setting, not just the inscribed circle (see MOON_FILL)
    /* a rendered night needs no drawing: the stage is already showing that exact night */
    if (hasMoonFrames(handle, metal)) return '';
    var d = window.COMoon.litPath(age, cx, cy, r);
    var src = moonFace(handle, metal);
    if (!src) {
      var tone = metal === 'vermeil' ? 'url(#cg-moon-g)' : 'url(#cg-moon-s)';
      return '<path d="' + d + '" fill="' + tone + '" stroke="rgba(245,240,232,0.35)" stroke-width="0.3"/>';
    }
    /* The boundary between silver and druzy is not a line. The stone is loose crystal, so single
       grains sit out on the silver and small bays of silver run back into the stone. Displacing a
       smooth edge only makes it wobble — it stays connected. So this DISSOLVES the edge instead:
       blur the shape into a ramp across the interlock band, add fractal noise, then threshold hard.
       Where ramp+noise crosses the threshold you get detached grains on one side and bays on the
       other, which is what the crystal frontier actually looks like. */
    var u = 'cgm' + (++moonUID);
    var band = r * EDGE.scatter, pad = band * 2 + 2;
    var rx = (cx - r - pad).toFixed(3), ry = (cy - r - pad).toFixed(3), rw = (2 * (r + pad)).toFixed(3);
    var reg = 'x="' + rx + '" y="' + ry + '" width="' + rw + '" height="' + rw + '"';
    var x = (cx - r).toFixed(3), y = (cy - r).toFixed(3), s2 = (r * 2).toFixed(3);
    var g = '<defs>' +
        '<filter id="' + u + 'r" filterUnits="userSpaceOnUse" ' + reg + '>' +
          '<feGaussianBlur in="SourceGraphic" stdDeviation="' + band.toFixed(3) + '" result="ramp"/>' +
          '<feTurbulence type="fractalNoise" baseFrequency="' + (1 / (EDGE.grain * r)).toFixed(4) +
            '" numOctaves="' + EDGE.oct + '" seed="' + EDGE.seed + '" result="n"/>' +
          /* Filter alpha is clamped to 0..1 after every primitive, so the noise is kept POSITIVE and
             blended as a weighted average with the ramp instead of being centred on zero — a
             centred version loses its whole negative half to the clamp and just shifts the edge. */
          '<feColorMatrix in="n" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0 0 0 0" result="na"/>' +
          '<feComposite in="ramp" in2="na" operator="arithmetic" k1="0" k2="' + (1 - EDGE.amp).toFixed(3) +
            '" k3="' + EDGE.amp.toFixed(3) + '" k4="0" result="mix"/>' +
          '<feComponentTransfer in="mix" result="thr">' +
            '<feFuncA type="linear" slope="' + EDGE.slope + '" intercept="' + (-EDGE.slope * 0.5).toFixed(2) +
            '"/></feComponentTransfer>' +
          '<feFlood flood-color="#fff" result="w"/>' +
          '<feComposite in="w" in2="thr" operator="in"/>' +
        '</filter>' +
        '<clipPath id="' + u + 'g" clipPathUnits="userSpaceOnUse">' +
          '<path clip-rule="evenodd" d="' + ring(cx, cy, r, r * (1 - EDGE.rim)) + '"/>' +
        '</clipPath>' +
        '<mask id="' + u + 'm" maskUnits="userSpaceOnUse" ' + reg + '>' +
          '<path d="' + d + '" fill="#fff" filter="url(#' + u + 'r)"/>' +
          /* the outer line, put back solid: the same shape drawn crisp, showing only inside the
             ring at the limb, so the dissolve is left to work on the terminator alone */
          '<path d="' + d + '" fill="#fff" clip-path="url(#' + u + 'g)"/>' +
        '</mask>';
    if (EDGE.shadow > 0) {
      g += '<filter id="' + u + 'f" x="-25%" y="-25%" width="150%" height="150%">' +
             '<feGaussianBlur stdDeviation="' + (r * 0.03).toFixed(3) + '"/></filter>';
    }
    g += '</defs>';
    if (EDGE.shadow > 0) {
      g += '<path d="' + d + '" fill="none" stroke="rgba(16,14,12,' + EDGE.shadow + ')" stroke-width="' +
             (r * 0.03).toFixed(3) + '" filter="url(#' + u + 'f)"/>';
    }
    return g +
      '<image href="' + esc(src) + '" xlink:href="' + esc(src) + '" x="' + x + '" y="' + y +
        '" width="' + s2 + '" height="' + s2 + '" preserveAspectRatio="none" opacity="' +
        (1 - MOON_FADE).toFixed(3) + '" mask="url(#' + u + 'm)"/>';
  }

  var DEFS =
    '<defs>' +
    /* uploaded artwork engraved: every tone becomes the engraving ink, and white becomes nothing.
       A filter, not mix-blend-mode, because a blend mode is dropped when the SVG is rasterized for the proof. */
    '<filter id="cg-art" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">' +
    '  <feColorMatrix type="matrix" values="0 0 0 0 0.161  0 0 0 0 0.149  0 0 0 0 0.137  -0.333 -0.333 -0.333 0 1" result="ink"/>' +
    '  <feComposite in="ink" in2="SourceGraphic" operator="in"/>' +   // ink only where the file has pixels
    '</filter>' +
    '<linearGradient id="cg-silver" x1="0" y1="0" x2="0.55" y2="1">' +
    '  <stop offset="0" stop-color="#F1F1ED"/><stop offset="0.55" stop-color="#C9C9C4"/><stop offset="1" stop-color="#A3A39E"/>' +
    '</linearGradient>' +
    '<linearGradient id="cg-gold" x1="0" y1="0" x2="0.55" y2="1">' +
    '  <stop offset="0" stop-color="#F0DCB0"/><stop offset="0.55" stop-color="#D6AE6B"/><stop offset="1" stop-color="#A8823F"/>' +
    '</linearGradient>' +
    '<linearGradient id="cg-moon-s" x1="0" y1="0" x2="0.6" y2="1">' +
    '  <stop offset="0" stop-color="#F0F0EC"/><stop offset="1" stop-color="#A9A9A4"/>' +
    '</linearGradient>' +
    '<linearGradient id="cg-moon-g" x1="0" y1="0" x2="0.6" y2="1">' +
    '  <stop offset="0" stop-color="#EED9AE"/><stop offset="1" stop-color="#B08A4F"/>' +
    '</linearGradient>' +
    '</defs>';

  /* letter tones per form + metal (Lock cipher constant; Vessel reversed; else follow metal) */
  /* Patti can state the metal the initials themselves are made in; 'auto' keeps the piece's own rule */
  /* The tone may be one value for the whole piece (legacy) or one PER METAL, because the right
     answer differs by variant: on the silver piece the initials might be silver, on the vermeil one
     two-tone (Kevin 9/16). Shaped like plates and boxes, so the bench writes it the same way. */
  function toneFor(tone, metal) {
    if (!tone || typeof tone === 'string') return tone || '';
    return tone[metal] || (metal === 'mixed' ? tone.silver : '') || tone.all || tone['default'] || '';
  }
  function toneSpec(formId, metal, tone) {
    tone = toneFor(tone, metal);
    var S = 'url(#cg-silver)', G = 'url(#cg-gold)';
    if (tone === 'silver') return [S, S];
    if (tone === 'vermeil') return [G, G];
    if (tone === 'mixed') return [S, G];
    if (tone === 'mixed-gold') return [G, S];
    return tones(formId, metal);
  }
  function tones(formId, metal) {
    var S = 'url(#cg-silver)', G = 'url(#cg-gold)';
    if (formId === 'entwined-lock') return [S, G];
    if (formId === 'entwined-vessel') return metal === 'vermeil' ? [S, S] : [G, G];
    if (metal === 'mixed') return [S, G];
    if (metal === 'vermeil') return [G, G];
    return [S, S];
  }

  /* the genuine Sappho interwoven cipher: lowercase+CAPITAL with ligatures on → interlaced glyph,
     layered Two (first fills) → One (second fills) → Regular (line work) */
  function monogram(letters, cx, cy, h, toneAB) {
    var L = (letters || '').replace(/[^a-zA-Z]/g, '').slice(0, 2);
    if (!L.length) return '';
    var attrs = 'x="' + cx + '" y="' + (cy + h * 0.36) + '" text-anchor="middle" font-size="' + h +
      '" style="font-feature-settings:\'liga\' 1">';
    var out = '';
    if (L.length === 1) {
      // Only one letter yet — render it EXACTLY as the first (tall) letter of the cipher: the
      // lowercase input FILLED by "Sappho Two" (the first-letter fill layer) with the "Sappho"
      // line-work on top. A lone first letter is identical to its form inside any pair, so this is
      // deterministic, and it's filled to match the finished piece rather than a hollow capital.
      var one = esc(L[0].toLowerCase());
      out += '<text font-family="Sappho Two" fill="' + toneAB[0] + '" ' + attrs + one + '</text>';
      out += '<text font-family="Sappho" fill="rgba(26,26,26,0.7)" ' + attrs + one + '</text>';
      return out;
    }
    var content = L[0].toLowerCase() + L[1].toUpperCase();
    out += '<text font-family="Sappho Two" fill="' + toneAB[0] + '" ' + attrs + esc(content) + '</text>';
    out += '<text font-family="Sappho One" fill="' + toneAB[1] + '" ' + attrs + esc(content) + '</text>';
    out += '<text font-family="Sappho" fill="rgba(26,26,26,0.7)" ' + attrs + esc(content) + '</text>';
    return out;
  }

  /* The Entwined Charm has no product plate — the piece IS the letters. Render them on a
     blank bone ground (Kevin: "just a blank image with the letters rendered"). */
  function charmStage(letters, metal) {
    var g = DEFS;
    g += '<rect x="0" y="0" width="100" height="100" fill="#EDE6D9"/>';
    var L = (letters || '').replace(/[^a-zA-Z]/g, '');
    if (L.length >= 1) {
      g += '<g class="cg-mono">' + monogram(letters, 50, 52, 48, tones('entwined-charm', metal)) + '</g>';
    }
    return g;
  }

  function engraveClass(font) { return font === 'caps' ? 'engrave-caps' : 'engrave-script'; }

  /* Resolve the engrave box for a metal. A form may carry per-metal boxes (engraveBoxByMetal)
     when its blank plates are framed differently per finish (e.g. the Lock — the silver and
     vermeil padlocks sit at different points on the plate); otherwise the single engraveBox is
     used for every metal. */
  function boxFor(cfg, metal) {
    return (cfg && cfg.engraveBoxByMetal && cfg.engraveBoxByMetal[metal]) || (cfg && cfg.engraveBox) || null;
  }

  /* overlay(cfg, state) → inner SVG string. cfg: {form, modes, engraveBox[, engraveBoxByMetal]}. state: {letters, night, words, font, metal} */
  function overlay(cfg, state) {
    var box = boxFor(cfg, state && state.metal);
    if (!box) return '';
    var mode = cfg.modes[0];
    var cx = box.x + box.w / 2, cy = box.y + box.h / 2;
    var g = DEFS + '<g transform="rotate(' + (box.rotate || 0) + ' ' + cx + ' ' + cy + ')">';

    if (mode === 'initials') {
      // wrapped so pdp.js can measure the real ink bbox and optically center it
      g += '<g class="cg-mono">' + monogram(state.letters, cx, cy, box.h * 0.95, tones(cfg.form, state.metal)) + '</g>';
    } else if (mode === 'date') {
      var d = nightDate(state.night);
      if (d && window.COMoon) {
        var r = Math.min(box.w, box.h) / 2;
        g += moonSVG(window.COMoon.age(d), cx, cy, r, state.handle || cfg.handle, state.metal);
      }
    } else if (mode === 'engraving') {
      var maxC = box.maxChars || 24;
      var raw = (state.words || '').slice(0, maxC);
      if (raw) {
        var isScript = state.font !== 'caps';
        var text = isScript ? raw : raw.toUpperCase();
        // One line until the text passes half the max characters, then break into TWO balanced
        // lines at the space nearest the middle (hard-split only if there's no space).
        var lines;
        // cfg.singleLine (e.g. the 5mm Slim band) never wraps: one line, sized to fit
        if (cfg.singleLine || text.length <= maxC / 2) {
          lines = [text];
        } else {
          var mid = Math.round(text.length / 2), split = -1, best = 1e9;
          for (var i = 0; i < text.length; i++) {
            if (text[i] === ' ') { var dd = Math.abs(i - mid); if (dd < best) { best = dd; split = i; } }
          }
          lines = split === -1 ? [text.slice(0, mid), text.slice(mid)]
                               : [text.slice(0, split), text.slice(split + 1)];
        }
        var L = lines.length;
        // approx glyph advance ÷ font-size. Caps (GFS Didot) carries letter-spacing 0.14em so it
        // needs a wider allowance than script (Pinyon), or it overflows the box.
        var ratio = isScript ? 0.36 : 0.82;
        var longest = lines.reduce(function (m, l) { return Math.max(m, l.length); }, 1);
        var lh = 1.15;
        // Fill the border: size to the border width (longest line) and height (line count), with
        // NO fixed cap — so the text starts as large as fits and only ever shrinks as you type
        // more or it wraps to two lines. The letters always stay inside the (calibratable) border.
        var fs = Math.min((box.w * 0.94) / (longest * ratio), (box.h * 0.92) / (L * lh));
        var startY = cy - (L * fs * lh) / 2 + fs * 0.78;
        var tspans = lines.map(function (l, k) {
          return '<tspan x="' + cx + '" y="' + (startY + k * fs * lh).toFixed(2) + '">' + esc(l) + '</tspan>';
        }).join('');
        g += '<text class="' + engraveClass(state.font) + '" text-anchor="middle" font-size="' + fs.toFixed(2) + '">' + tspans + '</text>';
      }
    }
    g += '</g>';
    return g;
  }

  /* ===================== v2: placements, image-percent boxes, line-by-line engraving ===================== */
  /* A v2 placement (see coeura-engrave.js) carries its own plate photo per metal and a box in IMAGE
     PERCENT: x/w are % of the photo's width, y/h % of its height. The overlay's viewBox is
     0 0 100 VH with VH = 100 * photoH / photoW, so a box maps 1:1 onto a photo of any aspect.
     Every legacy plate is square (VH = 100), so the old 0-100 boxes carry over unchanged. */
  var INK = 'rgba(26,26,26,0.55)';
  var REF = 1.3;          // floor reference width = average character advance x REF (see layoutText)

  /* Engraving faces. Self-hosted theme assets (coeura.css @font-face) so the live preview, the
     Engraving Bench and the add-to-cart proof all draw with the same files. `ls` is letter-spacing
     in em; `sample` gives the average character width used for the per-line size floor. */
  var FONTS = {
    script: { key: 'script', label: 'Script', family: 'Coeura Script', stack: "'Coeura Script', 'Pinyon Script', cursive", upper: false, ls: 0, sample: 'Always yours, the long way home' },
    caps: { key: 'caps', label: 'Small caps', family: 'Coeura Didot', stack: "'Coeura Didot', 'GFS Didot', serif", upper: true, ls: 0.14, sample: 'Always yours, the long way home' },
    serif: { key: 'serif', label: 'Serif', family: 'Coeura Serif', stack: "'Coeura Serif', Georgia, serif", upper: false, ls: 0.02, sample: 'Always yours, the long way home' },
    pitch: { key: 'pitch', label: 'Pitch', family: 'Pitch', stack: "'Pitch', 'Courier New', monospace", upper: false, ls: 0.04, sample: 'Always yours, the long way home' },
    zodiac: { key: 'zodiac', label: 'Moon sign', family: 'Coeura Zodiac', stack: "'Coeura Zodiac', 'Noto Sans Symbols', serif", upper: false, ls: 0, sample: '♈♉♊♋' }
  };
  /* font family -> theme asset (the proof embeds these so the rasterized SVG draws true) */
  var FONT_FILES = {
    'Coeura Script': 'engrave-script.woff2', 'Coeura Didot': 'engrave-didot.woff2', 'Coeura Serif': 'engrave-serif.woff2',
    'Pitch': 'pitch-regular.woff2', 'Coeura Zodiac': 'engrave-zodiac.woff2',
    'Sappho': 'mfc_sappho_mngm.otf', 'Sappho One': 'mfc_sappho_mngm_one.otf', 'Sappho Two': 'mfc_sappho_mngm_two.otf'
  };

  var mcan = null, MC = {};
  function mctx() { if (!mcan) mcan = document.createElement('canvas').getContext('2d'); return mcan; }
  function shown(text, key) { var f = FONTS[key] || FONTS.script; return f.upper ? String(text).toUpperCase() : String(text); }
  /* Patti can lock any lettering to capitals: text.caps lists the fonts engraved in caps for this composition */
  function capsOn(text, key) {
    var f = FONTS[key] || FONTS.script;
    if (f.upper) return true;
    var c = text && text.caps;
    return c === true || (Array.isArray(c) && c.indexOf(key) !== -1);
  }
  function cased(raw, key, text) { return capsOn(text, key) ? String(raw).toUpperCase() : String(raw); }
  /* width, ink ascent and ink descent of `text` at font-size 1, letter-spacing included (ACTUAL glyphs) */
  function measure(text, key) {
    var f = FONTS[key] || FONTS.script, t = shown(text, key), id = f.key + '|' + t;
    if (MC[id]) return MC[id];
    var c = mctx();
    c.font = '100px ' + f.stack;
    var m = c.measureText(t), n = Array.from(t).length;
    // fit on the wider of the advance width and the INK extent: script swashes (Pinyon's capitals) overhang
    // their advance, and the engraving area is a hard boundary on the piece
    var ink = (m.actualBoundingBoxLeft || 0) + (m.actualBoundingBoxRight || 0);
    var r = {
      w: (Math.max(m.width, ink) + f.ls * 100 * n) / 100,
      asc: (m.actualBoundingBoxAscent != null ? m.actualBoundingBoxAscent : 72) / 100,
      desc: (m.actualBoundingBoxDescent != null ? m.actualBoundingBoxDescent : 22) / 100
    };
    MC[id] = r;
    return r;
  }
  function metrics(key) { var f = FONTS[key] || FONTS.script; return measure(f.upper ? 'HQJ' : 'Hdlgjpqy', key); }
  function avgAdvance(key, up) { var f = FONTS[key] || FONTS.script, t = shown(up ? f.sample.toUpperCase() : f.sample, key); return measure(t, key).w / Array.from(t).length; }
  /* resolve once the named faces are really loaded (measurements under a fallback font are wrong) */
  function loadFonts(keys) {
    if (!document.fonts || !document.fonts.load) return Promise.resolve();
    var fams = (keys || Object.keys(FONTS)).map(function (k) { return FONTS[k]; }).filter(Boolean);
    return Promise.all(fams.map(function (f) {
      return document.fonts.load('40px "' + f.family + '"', f.upper ? 'HQ' : 'Hq').catch(function () {});
    })).then(function () { MC = {}; });
  }

  function pickBox(pl, key) {
    var b = pl && pl.boxes;
    if (!b) return null;
    return b[key] || b.all || b['default'] || b.silver || b.vermeil || b[Object.keys(b)[0]] || null;
  }
  function unitBox(b, vh) {
    var k = (vh || 100) / 100;
    return { x: +b.x, y: b.y * k, w: +b.w, h: b.h * k, rotate: +b.rotate || 0 };
  }
  /* the font a configured line renders in: a line's locked font, else the customer's pick, else the first offered */
  function lineFont(text, i, chosen) {
    var l = (text.lines && text.lines[i]) || {};
    if (l.font && FONTS[l.font]) return l.font;
    if (chosen && FONTS[chosen] && (text.fonts || []).indexOf(chosen) !== -1) return chosen;
    return (text.fonts && text.fonts[0]) || 'script';
  }

  /* Lay out the lines of one engraving placement inside its box (viewBox units).
     entries[i] = { text, t } for configured line i; t in [0,1] is the customer's size slider (1 = largest).
     Rules (Kevin + Patti, 9/10 call):
       - floor per line = the size at which that line's character LIMIT spans the box width, so every
         legal line can be engraved at or above it; if the lines at their floors don't stack inside the
         box height, all floors scale down together (config-determined, never text-dependent)
       - ceiling per line = the largest size its ACTUAL glyphs fit the width, capped by maxPct of the
         single-line height; the slider picks between floor and ceiling (default: largest)
       - a line inside its limit but too WIDE for its font never shrinks below the floor: it is flagged
         ('wide') so the composer asks the customer to shorten it or move a word to the next line
       - one line centers; several lines center together as a block; if the block is too tall the size
         above each line's floor is given back proportionally
       - direction 'up'/'down' rotates the block to run vertically; box.rotate adds a fine angle */
  function layoutText(ub, text, entries, chosen) {
    var vert = !text.noRotate && (text.orientation === 'up' || text.orientation === 'down');
    var W = (vert ? ub.h : ub.w) * 0.96, H = (vert ? ub.w : ub.h) * 0.94;
    var LH = text.lh || 1.25;
    var cfgLines = text.lines && text.lines.length ? text.lines : [{ limit: 14 }];
    var mins = [], sumMin = 0, i;
    for (i = 0; i < cfgLines.length; i++) {
      // x REF: measured 9/11, realistic strings at their limit run up to ~1.17x the average advance (dates in
      // script ~1.27x), so the floor uses 1.3x; only genuinely wide strings (WWWW...) are asked to shorten
      var lfk = lineFont(text, i, chosen);
      var mn = W / (avgAdvance(lfk, capsOn(text, lfk)) * REF * Math.max(1, cfgLines[i].limit || 14));
      mins.push(mn); sumMin += mn * LH;
    }
    if (sumMin > H) { var s0 = H / sumMin; mins = mins.map(function (m) { return m * s0; }); }
    /* Paragraph form: one size for the whole block, the size a FULL line takes, so the lettering
       stays even and never grows as the customer types less. The block still centres. */
    if (text.paragraph && mins.length) {
      var uni = Math.min.apply(null, mins);
      mins = mins.map(function () { return uni; });
    }

    var out = [], warns = [];
    for (i = 0; i < cfgLines.length; i++) {
      var e = entries && entries[i], raw = e && e.text ? String(e.text) : '';
      if (!raw.trim()) continue;
      var fk = lineFont(text, i, chosen), mm = metrics(fk);
      raw = cased(raw, fk, text);                       // capitals lock: measured and drawn as engraved
      var ms = measure(raw, fk);
      var widthFit = W / Math.max(ms.w, 1e-6);
      /* a line may cap its own size: on "Name and moon sign" the sign sits beside the name and rarely
         wants the same height as it. A line with none follows the composition's largest size. */
      var lpct = cfgLines[i].maxPct == null ? (text.maxPct || 100) : cfgLines[i].maxPct;
      var cap = (H / Math.max(mm.asc + mm.desc, 0.5)) * (lpct / 100);
      var lo = Math.min(mins[i], cap), hi = text.paragraph ? lo : Math.min(widthFit, cap);
      var wide = widthFit < lo * 0.995;
      if (wide) warns.push({ line: i, kind: 'wide' });
      var t = text.slider === false ? 1 : (e.t == null ? 1 : Math.max(0, Math.min(1, +e.t)));
      // a too-wide line is DRAWN shrunk to fit (so the preview stays on the piece) but stays flagged: the
      // composer blocks the order and asks for fewer words. `floor` keeps the configured minimum for readouts.
      out.push(wide
        ? { i: i, text: shown(raw, fk), font: fk, fs: widthFit, lo: widthFit, hi: widthFit, floor: lo, asc: mm.asc, desc: mm.desc, wide: true }
        : { i: i, text: shown(raw, fk), font: fk, fs: lo + t * Math.max(0, hi - lo), lo: lo, hi: Math.max(lo, hi), floor: lo, asc: mm.asc, desc: mm.desc, wide: false });
    }
    /* Match the smallest line: the line that needs the most room sets the size, and the others come
       down to it, so three typed lines read as one even block instead of short lines ballooning. */
    function matchDown() {
      if (!text.matchSmallest || out.length < 2) return;
      var m = Math.min.apply(null, out.map(function (l) { return l.fs; }));
      out.forEach(function (l) { l.fs = m; });
    }
    matchDown();
    var total = out.reduce(function (a, l) { return a + l.fs * LH; }, 0);
    if (total > H && out.length) {
      // a line already at or below its floor (matched down, or too wide and shrunk to fit) is never pushed back up
      var lof = function (l) { return Math.min(l.lo, l.fs); };
      var floor = out.reduce(function (a, l) { return a + lof(l) * LH; }, 0), excess = total - floor;
      var k = excess > 0 ? Math.max(0, H - floor) / excess : 0;
      out.forEach(function (l) { var lo = lof(l); l.fs = lo + (l.fs - lo) * k; });
      matchDown();                                    // the height give-back must not break the match
      total = out.reduce(function (a, l) { return a + l.fs * LH; }, 0);
    }
    var cx = ub.x + ub.w / 2, cy = ub.y + ub.h / 2, y = cy - total / 2;
    var mmPer = text.fieldMm ? text.fieldMm / ub.w : 0;
    out.forEach(function (l) {
      var band = l.fs * LH, inkH = (l.asc + l.desc) * l.fs;
      l.x = cx;
      l.y = y + (band - inkH) / 2 + l.asc * l.fs;      // baseline: ink optically centred in its band
      y += band;
      if (mmPer) { var capR = measure('H', l.font).asc; l.capMm = l.fs * capR * mmPer; l.loMm = l.floor * capR * mmPer; }
    });
    var rot = (ub.rotate || 0) + (!vert ? 0 : text.orientation === 'up' ? -90 : 90);
    return { lines: out, warns: warns, cx: cx, cy: cy, rotate: rot, W: W, H: H, LH: LH };
  }

  /* Patti's own moon-sign glyphs (THE MOON GLYPHS 7.7.26), cut out of her artwork sheet by
     src/scripts/coeura-glyphs/extract_glyphs.py as flat silhouettes. They replace the
     'Coeura Zodiac' font, whose shapes are a different design: her Aquarius is rounded waves where
     the font's is an angular zigzag, and her Cancer spirals curl open. Drawn as an <image> and
     recoloured to engraving ink, so one filter serves every sign at any size. The font stays as the
     fallback for anything not in this set.
     SIGN_REV dates the cut, not the artwork — a theme image has no ?v=, so a changed glyph has to
     arrive under a new filename or browsers keep serving the old one. The 0916 set is the first
     that holds her weight: the cut before it ran out into the warm skirt around each wire and came
     out a third too heavy, with Cancer's spirals closed and Leo's ring down to a dot. */
  var SIGN_REV = '0916-';
  var SIGN_IDS = ['aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo',
                  'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces'];
  var SIGN_CHARS = '♈♉♊♋♌♍♎♏♐♑♒♓';
  function signIdOf(text) {
    var i = SIGN_CHARS.indexOf(String(text || '').charAt(0));
    return i < 0 ? null : SIGN_IDS[i];
  }
  /* the one place a glyph's file is named — coeura-engrave.js and coeura-handoff.js show the same
     artwork beside the readout, and all three have to move together when the cut changes */
  function signArt(id) { return ASSET_BASE + 'moonsign-' + SIGN_REV + id + '.png'; }
  var inkFilterUID = 0;
  /* the glyph art is square; it is placed on the line's own em box so it sits where the text would */
  function signSVG(id, cx, baseline, fs) {
    var u = 'cgs' + (++inkFilterUID);
    var box = fs * 1.02, x = cx - box / 2, y = baseline - fs * 0.78;
    return '<defs><filter id="' + u + '" color-interpolation-filters="sRGB">' +
        '<feColorMatrix type="matrix" values="0 0 0 0 0.161  0 0 0 0 0.149  0 0 0 0 0.137  0 0 0 1 0"/>' +
      '</filter></defs>' +
      '<image href="' + esc(signArt(id)) +
        '" xlink:href="' + esc(signArt(id)) +
        '" x="' + x.toFixed(3) + '" y="' + y.toFixed(3) +
        '" width="' + box.toFixed(3) + '" height="' + box.toFixed(3) +
        '" filter="url(#' + u + ')" style="mix-blend-mode:multiply"/>';
  }

  function textSVG(L) {
    var g = '<g transform="rotate(' + L.rotate.toFixed(2) + ' ' + L.cx.toFixed(3) + ' ' + L.cy.toFixed(3) + ')">';
    L.lines.forEach(function (l) {
      var f = FONTS[l.font] || FONTS.script;
      var sid = l.font === 'zodiac' ? signIdOf(l.text) : null;
      if (sid) { g += signSVG(sid, l.x, l.y, l.fs); return; }
      var sp = f.ls ? f.ls * l.fs : 0;
      g += '<text x="' + (l.x + sp / 2).toFixed(3) + '" y="' + l.y.toFixed(3) + '" text-anchor="middle"' +
        ' font-family="' + f.stack + '" font-size="' + l.fs.toFixed(3) + '"' +
        (sp ? ' letter-spacing="' + sp.toFixed(3) + '"' : '') +
        ' fill="' + INK + '" style="mix-blend-mode:multiply">' + esc(l.text) + '</text>';
    });
    return g + '</g>';
  }

  /* Plain initials in the serif face, two or three, with monogram spacing: "A | H", and for three a rule
     under the pair with the third letter below (REV 4 sheet, option 06). Not the Sappho interlaced cipher. */
  function monogramPlain(mg, letters, ub) {
    mg = mg || {};
    var key = FONTS[mg.font] ? mg.font : 'serif', f = FONTS[key];
    var count = mg.count === 3 ? 3 : 2;
    var L = String(letters || '').replace(/[^a-zA-Z]/g, '').toUpperCase().split('').slice(0, count);
    if (!L.length) return { svg: '', layout: null };
    var track = mg.tracking == null ? 0.5 : +mg.tracking;        // space each side of the separator, in ems
    var mm = metrics(key), pair = L.slice(0, 2);
    var wPair = pair.reduce(function (a, c) { return a + measure(c, key).w; }, 0) + (pair.length > 1 ? 2 * track : 0);
    var W = ub.w * 0.96, H = ub.h * 0.94;
    var rows = count === 3 && L.length === 3 ? 2 : 1;
    var gap = mg.rule === false ? 0.34 : 0.62;                    // room for the rule between the rows
    var hFactor = rows === 2 ? 2 * (mm.asc + mm.desc) + gap : (mm.asc + mm.desc);
    var fs = Math.min(W / Math.max(wPair, 0.001), H / hFactor) * ((mg.maxPct || 100) / 100);
    var cx = ub.x + ub.w / 2, cy = ub.y + ub.h / 2;
    var total = fs * hFactor, top = cy - total / 2;
    var g = '<g transform="rotate(' + (ub.rotate || 0) + ' ' + cx.toFixed(3) + ' ' + cy.toFixed(3) + ')">';
    function put(txt, x, baseline) {
      return '<text x="' + x.toFixed(3) + '" y="' + baseline.toFixed(3) + '" text-anchor="middle" font-family="' + f.stack +
        '" font-size="' + fs.toFixed(3) + '" fill="' + INK + '" style="mix-blend-mode:multiply">' + esc(txt) + '</text>';
    }
    var base1 = top + mm.asc * fs, x = cx - (wPair * fs) / 2;
    pair.forEach(function (c, i) {
      var w = measure(c, key).w * fs;
      g += put(c, x + w / 2, base1);
      x += w + (i === 0 ? 2 * track * fs : 0);
    });
    if (pair.length > 1 && mg.separator !== false) {             // the hairline between the two letters
      var sy = base1 - mm.asc * fs * 0.86, sh = mm.asc * fs * 0.86;
      g += '<rect x="' + (cx - fs * 0.018).toFixed(3) + '" y="' + sy.toFixed(3) + '" width="' + (fs * 0.036).toFixed(3) +
        '" height="' + sh.toFixed(3) + '" fill="' + INK + '" style="mix-blend-mode:multiply"/>';
    }
    if (rows === 2) {
      var ry = top + (mm.asc + mm.desc) * fs + gap * fs * 0.42, rw = wPair * fs * 0.92;
      if (mg.rule !== false) {
        g += '<rect x="' + (cx - rw / 2).toFixed(3) + '" y="' + ry.toFixed(3) + '" width="' + rw.toFixed(3) +
          '" height="' + (fs * 0.045).toFixed(3) + '" fill="' + INK + '" style="mix-blend-mode:multiply"/>';
      }
      g += put(L[2], cx, top + (mm.asc + mm.desc) * fs + gap * fs + mm.asc * fs);
    }
    return { svg: g + '</g>', layout: { lines: [{ i: 0, text: L.join(' '), font: key, fs: fs, lo: fs, hi: fs, floor: fs, asc: mm.asc, desc: mm.desc, wide: false }], warns: [], cx: cx, cy: cy, rotate: ub.rotate || 0, W: W, H: H, LH: 1 } };
  }

  /* renderPlacement(pl, st, o) -> { svg, layout, box } for one v2 placement.
     st: { letters, night, lines:[{text,t}], font }
     o:  { boxKey (which metal's box), metal (plate metal, drives the moon tone), toneMetal (letter tones;
           'mixed' is kept only for the blank charm), vh (viewBox height), form (tones per form) } */
  /* "Match every line to one size" is the CUSTOMER's toggle, so their choice overrides the stored default */
  function withMatch(text, st) {
    if (!st || st.match == null || !!st.match === !!text.matchSmallest) return text;
    var c = {}, k;
    for (k in text) if (Object.prototype.hasOwnProperty.call(text, k)) c[k] = text[k];
    c.matchSmallest = !!st.match;
    return c;
  }
  function renderPlacement(pl, st, o) {
    st = st || {}; o = o || {};
    var b = pickBox(pl, o.boxKey || o.metal);
    if (!b) return { svg: DEFS, layout: null, box: null };
    var ub = unitBox(b, o.vh), cx = ub.x + ub.w / 2, cy = ub.y + ub.h / 2, g = DEFS, lay = null;
    if (pl.mode === 'initials') {
      g += '<g transform="rotate(' + ub.rotate + ' ' + cx + ' ' + cy + ')"><g class="cg-mono">' +
        monogram(st.letters, cx, cy, ub.h * 0.95, toneSpec(o.form, o.toneMetal || o.metal, o.tone)) + '</g></g>';
    } else if (pl.mode === 'date') {
      var d = nightDate(st.night);
      if (d && window.COMoon) {
        var r = Math.min(ub.w, ub.h) / 2;
        g += moonSVG(window.COMoon.age(d), cx, cy, r, o.handle, o.metal);
      }
    } else if (pl.mode === 'monogram') {
      var mp = monogramPlain(pl.monogram, st.letters, ub);
      g += mp.svg; lay = mp.layout;
    } else if (pl.mode === 'upload') {
      // the customer's own artwork, fitted inside the area and printed like an engraving
      if (st.art) {
        g += '<g transform="rotate(' + (ub.rotate || 0) + ' ' + cx + ' ' + cy + ')">' +
          '<image href="' + esc(st.art) + '" x="' + ub.x.toFixed(3) + '" y="' + ub.y.toFixed(3) +
          '" width="' + ub.w.toFixed(3) + '" height="' + ub.h.toFixed(3) +
          '" preserveAspectRatio="xMidYMid meet" filter="url(#cg-art)" opacity="0.95"/></g>';
      }
    } else if (pl.mode === 'engraving' && pl.text) {
      lay = layoutText(ub, withMatch(pl.text, st), st.lines || [], st.font);
      g += textSVG(lay);
    }
    return { svg: g, layout: lay, box: ub };
  }

  window.CarigramRender = {
    overlay: overlay, monogram: monogram, charmStage: charmStage, tones: tones, boxFor: boxFor, DEFS: DEFS,
    FONTS: FONTS, FONT_FILES: FONT_FILES, INK: INK, esc: esc, toneSpec: toneSpec, toneFor: toneFor,
    measure: measure, metrics: metrics, loadFonts: loadFonts, lineFont: lineFont, capsOn: capsOn,
    pickBox: pickBox, unitBox: unitBox, layoutText: layoutText, renderPlacement: renderPlacement,
    signIdOf: signIdOf, SIGN_IDS: SIGN_IDS, signArt: signArt, withMatch: withMatch,
    get SIGN_REV() { return SIGN_REV; }, set SIGN_REV(v) { SIGN_REV = v; },  // tools/moonsign_qa.html

    MOON_FACES: MOON_FACES, moonFace: moonFace, moonSVG: moonSVG, ASSET_BASE: ASSET_BASE, EDGE: EDGE,
    MOON_FRAMES: MOON_FRAMES, moonFrame: moonFrame, hasMoonFrames: hasMoonFrames, nightDate: nightDate,
    displayAge: displayAge, get MIN_ILLUM() { return MIN_ILLUM; },
    get MOON_FADE() { return MOON_FADE; }, set MOON_FADE(v) { MOON_FADE = v; },
    monogramPlain: monogramPlain
  };
})();
